<template>
  <div class="inv-compact-page">
    <div class="inv-toolbar">
      <div class="inv-toolbar__title">出库日志</div>
      <div class="inv-toolbar__actions">
        <el-upload class="inv-upload" :show-file-list="false" :before-upload="handleImportExcel" accept=".xls,.xlsx">
          <el-button size="small">导入Excel</el-button>
        </el-upload>
        <el-button size="small" @click="exportExcel">导出Excel</el-button>
        <el-button size="small" type="text" @click="handleDownloadTemplate">下载模板</el-button>
      </div>
    </div>

    <div class="inv-compact-panel">
      <div class="inv-compact-filter">
        <el-select v-model="filters.region" placeholder="使用区域" size="small" style="width: 120px;">
          <el-option label="全部区域" value="all" />
          <el-option v-for="r in regionOptions" :key="r" :label="r" :value="r" />
        </el-select>
        <el-input
          v-model="filters.operator"
          placeholder="操作人"
          size="small"
          clearable
          style="width: 120px;"
        />
        <el-input
          v-model="filters.keyword"
          placeholder="商品名称/备注"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          style="width: 180px;"
          @keyup.enter.native="search"
        />
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始"
          end-placeholder="结束"
          size="small"
          style="width: 240px;"
        />
        <el-button type="primary" size="small" icon="el-icon-search" @click="search">筛选</el-button>
        <el-button size="small" @click="reset">重置</el-button>
      </div>

      <el-table
        :data="list"
        border
        stripe
        size="small"
        style="width: 100%;"
        class="employee-table"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="operationTime" label="操作时间" min-width="160" sortable="custom">
          <template slot-scope="scope">{{ $formatDateTime(scope.row.operationTime) }}</template>
        </el-table-column>
        <el-table-column prop="logTypeLabel" label="类型" width="110" sortable="custom" />
        <el-table-column prop="productName" label="商品名称" min-width="140" sortable="custom" show-overflow-tooltip />
        <el-table-column prop="quantityDisplay" label="数量" width="90" sortable="custom" />
        <el-table-column prop="usageArea" label="区域" width="100" sortable="custom" />
        <el-table-column prop="operator" label="操作人" width="100" sortable="custom" />
        <el-table-column prop="remark" label="备注" min-width="120" sortable="custom" show-overflow-tooltip />
      </el-table>

      <div class="inv-compact-summary">
        <div class="inv-compact-summary-item">
          <div class="label">总数量</div>
          <div class="value">{{ summary.totalQuantity }}</div>
        </div>
        <div class="inv-compact-summary-item">
          <div class="label">操作次数</div>
          <div class="value">{{ summary.operationCount }}</div>
        </div>
      </div>

      <admin-pagination
        :total="total"
        :page.sync="currentPage"
        :page-size.sync="pageSize"
        @change="onPaginationChange"
      />
    </div>
  </div>
</template>

<script>
import {
  fetchInventoryRegions,
  fetchInventoryLogs,
  downloadInventoryTemplate,
  importInventoryExcel
} from '../api/inventory'
import { INVENTORY_USAGE_AREAS } from '../constants/inventory'
import { getUserInfo } from '../utils/auth'
import { triggerBlobDownload } from '../utils/fileExport'
import { guardUploadFile, formatImportErrors as formatImportErrorsText, showImportResult } from '../utils/fileImport'

export default {
  name: 'InventoryOutboundLogs',
  data() {
    const userInfo = getUserInfo()
    return {
      regions: [],
      advancedOpen: true,
      sortProp: '',
      sortOrder: '',
      filters: {
        region: 'all',
        operator: userInfo && userInfo.username ? userInfo.username : '',
        keyword: ''
      },
      dateRange: [],
      list: [],
      total: 0,
      pageSize: 30,
      currentPage: 1,
      summary: { totalQuantity: 0, operationCount: 0 }
    }
  },
  computed: {
    regionOptions() {
      const set = new Set([...INVENTORY_USAGE_AREAS, ...(this.regions || [])])
      return Array.from(set)
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      const regionsResp = await fetchInventoryRegions()
      this.regions = regionsResp && regionsResp.data ? regionsResp.data : []

      await this.fetchLogs()
    },

    getRangePayload() {
      if (!this.dateRange || !this.dateRange.length) return {}
      return {
        startDate: this.dateRange[0] ? this.dateRange[0].toISOString() : '',
        endDate: this.dateRange[1] ? this.dateRange[1].toISOString() : ''
      }
    },

    async fetchLogs() {
      const rangePayload = this.getRangePayload()
      try {
        const resp = await fetchInventoryLogs({
          type: 'out',
          region: this.filters.region,
          category: 'all',
          operator: this.filters.operator,
          keyword: this.filters.keyword,
          ...rangePayload,
          page: this.currentPage,
          pageSize: this.pageSize
        })

        if (resp && resp.code === 200 && resp.data) {
          this.list = resp.data.list || []
          this.total = resp.data.total || 0
          this.summary = resp.data.summary || this.summary
        } else {
          this.list = []
          this.total = 0
        }
      } catch (e) {
        this.list = []
        this.total = 0
        this.summary = { totalQuantity: 0, operationCount: 0 }
      }

      this.sortList()
    },

    async search() {
      this.currentPage = 1
      await this.fetchLogs()
    },

    async reset() {
      const userInfo = getUserInfo()
      const operator = userInfo && userInfo.username ? userInfo.username : ''
      this.filters = { region: 'all', operator, keyword: '' }
      this.dateRange = []
      this.currentPage = 1
      await this.fetchLogs()
    },

    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchLogs()
    },
    onPaginationChange({ page, pageSize }) {
      this.currentPage = page
      this.pageSize = pageSize
      this.fetchLogs()
    },

    async handleDownloadTemplate() {
      try {
        await downloadInventoryTemplate()
      } catch (e) {
        this.$message.error('模板下载失败')
      }
    },

    async exportExcel() {
      try {
        const header = ['操作时间', '日志类型', '商品名称', '数量', '使用区域', '操作人', '备注']
        const escapeCsv = (s) => {
          const str = String(s !== undefined && s !== null ? s : '')
          return `"${str.replace(/"/g, '""')}"`
        }
        const rows = (this.list || []).map((r) => [
          r.operationTime,
          r.logTypeLabel,
          r.productName,
          r.quantityDisplay != null ? r.quantityDisplay : r.quantity,
          r.usageArea || r.region,
          r.operator,
          r.remark
        ])
        const csv = [header.map(escapeCsv).join(','), ...rows.map((x) => x.map(escapeCsv).join(','))].join('\n')
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
        triggerBlobDownload(blob, '出库日志统计_导出', 'csv')
      } catch (e) {
        this.$message.error('导出失败')
      }
    },

    formatImportErrors(result) {
      return formatImportErrorsText(result)
    },

    async handleImportExcel(file) {
      if (!guardUploadFile(this, file)) return false
      try {
        const userInfo = getUserInfo()
        const op = userInfo && userInfo.username ? userInfo.username : '管理员'
        const resp = await importInventoryExcel(file, op)
        if (resp && resp.code === 200) {
          showImportResult(this, resp, { successMessage: '导入成功' })
          await this.fetchLogs()
        } else if (resp && resp.code === 400 && resp.data && resp.data.errors) {
          this.$alert(formatImportErrorsText(resp.data) || resp.message || '导入失败', '导入校验失败', { type: 'error' })
        } else {
          this.$message.error((resp && resp.message) || '导入失败')
        }
      } catch (e) {
        const data = e && e.response && e.response.data
        if (data && (data.message || (data.data && data.data.errors))) {
          this.$alert(formatImportErrorsText(data.data) || data.message, '导入校验失败', { type: 'error' })
        } else {
          this.$message.error((data && data.message) || '导入失败')
        }
      }
      return false
    },

    sortList() {
      if (!this.sortProp || !this.sortOrder) return
      const prop = this.sortProp
      const orderFactor = this.sortOrder === 'ascending' ? 1 : -1

      const sorted = (this.list || []).slice().sort((a, b) => {
        const va = a ? a[prop] : undefined
        const vb = b ? b[prop] : undefined

        if (va === undefined || va === null) return 1
        if (vb === undefined || vb === null) return -1

        const numA = typeof va === 'number' ? va : Number(va)
        const numB = typeof vb === 'number' ? vb : Number(vb)
        const bothNumeric = Number.isFinite(numA) && Number.isFinite(numB)

        const left = bothNumeric ? numA : String(va)
        const right = bothNumeric ? numB : String(vb)

        if (left > right) return 1 * orderFactor
        if (left < right) return -1 * orderFactor
        return 0
      })

      this.list = sorted
    },

    handleSortChange({ prop, order }) {
      this.sortProp = prop
      this.sortOrder = order
      this.sortList()
    }
  }
}
</script>

<style scoped>
.inv-log-card-header {
  flex-wrap: wrap;
}

.inv-log-card-header__actions {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 220px;
}

.employee-management > .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.filter-topbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.inventory-filter-form {
  width: 100%;
  overflow-x: auto;
}

.filter-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
}

.filter-row-1 {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(150px, 1fr));
  align-items: start;
  column-gap: 20px;
}

.filter-row-2 {
  width: 100%;
  margin-top: 16px;
}

.inventory-filter-form ::v-deep .el-form-item {
  margin-bottom: 0;
}

.inventory-filter-form.inventory-filter-form--stacked ::v-deep .el-form-item {
  margin-bottom: 12px;
}

.inventory-filter-form ::v-deep .el-form-item__label {
  white-space: nowrap;
  line-height: 1.3;
  padding-bottom: 4px;
}

.inventory-filter-form ::v-deep .el-form-item__content {
  margin-left: 0 !important;
}

.filter-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.bottom-summary {
  margin-top: 20px;
  display: flex;
  gap: 14px;
}

.bottom-summary-item {
  flex: 1;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--shadow-soft);
  padding: 14px 12px;
}

.bottom-summary-label {
  font-size: 12px;
  color: #4e5969;
  margin-bottom: 8px;
}

.bottom-summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #1d2129;
}

.pagination {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}
</style>

